import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { qk } from '../keys';
import type { TripStatus } from '@kmdx/contracts';
import type { Paginated, TripDetail, TripFilters, TripListItem, TripSheet, TripSuggestion, VehicleOverview } from '../types';

export interface RoutePoint {
  ts: string;
  latE7: number;
  lonE7: number;
  speedKph: number | null;
  country: string | null;
}

export interface TripRoute {
  tripId: string;
  points: RoutePoint[];
}

function toQuery(filters: TripFilters): Record<string, string | number | string[]> {
  const query: Record<string, string | number | string[]> = {};
  if (filters.status && filters.status.length > 0) query.status = filters.status;
  if (filters.vehicleId) query.vehicleId = filters.vehicleId;
  if (filters.driverId) query.driverId = filters.driverId;
  if (filters.fromDate) query.fromDate = filters.fromDate;
  if (filters.toDate) query.toDate = filters.toDate;
  if (filters.q) query.q = filters.q;
  query.page = filters.page ?? 1;
  query.perPage = filters.perPage ?? 50;
  return query;
}

export function useTrips(filters: TripFilters) {
  return useQuery({
    queryKey: qk.trips.list(filters),
    queryFn: () => api.get<Paginated<TripListItem>>('/trips', toQuery(filters)),
    placeholderData: (prev) => prev,
  });
}

export function useTrip(id: string | undefined) {
  return useQuery({
    queryKey: qk.trips.detail(id ?? ''),
    queryFn: () => api.get<TripDetail>(`/trips/${id}`),
    enabled: Boolean(id),
  });
}

/** Точките за повторение на маршрута. Сървърът ги разрежда — 11 буса, не 11 000. */
export function useTripRoute(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.trips.route(id ?? ''),
    queryFn: () => api.get<TripRoute>(`/trips/${id}/route`),
    enabled: Boolean(id) && enabled,
    staleTime: 5 * 60_000,
  });
}

/** Целият курс на един лист: заявки, товар, документи, гориво, заплата. */
export function useTripSheet(id: string | undefined) {
  return useQuery({
    queryKey: qk.trips.sheet(id ?? ''),
    queryFn: () => api.get<TripSheet>(`/trips/${id}/sheet`),
    enabled: Boolean(id),
  });
}

/** Курсовете на един бус — текущ, предстоящи, последни. */
export function useVehicleOverview(vehicleId: string | undefined) {
  return useQuery({
    queryKey: qk.trips.ofVehicle(vehicleId ?? ''),
    queryFn: () => api.get<VehicleOverview>(`/trips/of-vehicle/${vehicleId}`),
    enabled: Boolean(vehicleId),
  });
}

function useTripInvalidation() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: qk.trips.all });
    void queryClient.invalidateQueries({ queryKey: qk.orders.all });
    void queryClient.invalidateQueries({ queryKey: qk.board.all });
    void queryClient.invalidateQueries({ queryKey: qk.dispatch.all });
  };
}

/** Смяна на състоянието на курса — изпращане към шофьора, отказ. */
export function useTripTransition(tripId: string) {
  const invalidate = useTripInvalidation();
  return useMutation({
    mutationFn: (input: { to: TripStatus; reason?: string; singleOrderReason?: string }) =>
      api.post<TripDetail>(`/trips/${tripId}/status`, input),
    onSuccess: invalidate,
  });
}

/** Още една заявка в курса — така бусът не тръгва полупразен. */
export function useAttachOrder(tripId: string) {
  const invalidate = useTripInvalidation();
  return useMutation({
    mutationFn: (input: { orderId: string; allowVolumeOverflow?: boolean }) =>
      api.post<TripDetail>(`/trips/${tripId}/orders`, { revenueShareBps: 10000, ...input }),
    onSuccess: invalidate,
  });
}

export function useDetachOrder(tripId: string) {
  const invalidate = useTripInvalidation();
  return useMutation({
    mutationFn: (orderId: string) => api.del<TripDetail>(`/trips/${tripId}/orders/${orderId}`),
    onSuccess: invalidate,
  });
}

/** Чакащите заявки, които се побират в курса и са по пътя. */
export function useTripSuggestions(tripId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...qk.trips.sheet(tripId ?? ''), 'suggestions'],
    queryFn: () => api.get<TripSuggestion[]>(`/trips/${tripId}/suggestions`),
    enabled: Boolean(tripId) && enabled,
  });
}
